import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, Alert, Button, Eyebrow, Spinner, formatCountdown, money } from '@busbook/ui';
import { sumCents, type SeatCell, type SeatState } from '@busbook/shared';
import { useTenant } from '../lib/tenant.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

/** Fixed palette a tier's name is hashed into, so its colour stays stable across renders.
 * Mirrors the tenant-admin layout editor's tier-color approach (hash into a shared palette),
 * keyed by tierName here since the public seat map exposes names, not tier ids. */
const TIER_PALETTE = [
  '#2563eb', // blue
  '#16a34a', // green
  '#d97706', // amber
  '#db2777', // pink
  '#7c3aed', // violet
  '#0d9488', // teal
];

function hashTierColor(tierName: string): string {
  let hash = 0;
  for (let i = 0; i < tierName.length; i++) {
    hash = (hash * 31 + tierName.charCodeAt(i)) >>> 0;
  }
  return TIER_PALETTE[hash % TIER_PALETTE.length]!;
}

/**
 * Seat picker (docs/05 §2). Selecting a seat takes a real hold on the server, so
 * what you see is what is actually available; the countdown is the truth about
 * how long that hold lasts, not a decoration.
 *
 * Accessibility: every seat is a real button with an ARIA label, so the map is
 * fully operable by keyboard and screen reader.
 */

interface SeatMapSeat extends SeatCell {
  state: SeatState;
  heldByYou: boolean;
  priceCents: number;
  tierName: string;
}

interface SeatMapResponse {
  tripId: string;
  rows: number;
  columns: number;
  capacity: number;
  seatsLeft: number;
  seats: SeatMapSeat[];
  holdExpiresAt: string | null;
  maxSeatsPerBooking: number;
}

interface TripInfo {
  routeName: string;
  busName: string;
  departureAt: string;
  note: string | null;
  stops: Array<{ stopId: string; name: string; city: string; arrivalOffsetMin: number }>;
}

export function SeatSelection() {
  const t = useT();
  const { id: tripId = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useTenant();

  const [map, setMap] = useState<SeatMapResponse | null>(null);
  const [trip, setTrip] = useState<TripInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const liveRegion = useRef<HTMLParagraphElement>(null);

  const wanted = Number(params.get('passengers') ?? '1');
  const fromStopId = params.get('from') ?? '';
  const toStopId = params.get('to') ?? '';

  const load = useCallback(async () => {
    try {
      const [seatMap, tripInfo] = await Promise.all([
        api.request<SeatMapResponse>(
          `/public/trips/${tripId}/seatmap?fromStopId=${fromStopId}&toStopId=${toStopId}`,
          { method: 'GET', auth: false },
        ),
        api.request<TripInfo>(`/public/trips/${tripId}`, { method: 'GET', auth: false }),
      ]);
      setMap(seatMap);
      setTrip(tripInfo);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the seat map.');
    }
  }, [tripId, fromStopId, toStopId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Drives the hold countdown.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const selected = useMemo(
    () => (map?.seats ?? []).filter((seat) => seat.heldByYou).map((seat) => seat.code),
    [map],
  );

  const total = sumCents(
    (map?.seats ?? []).filter((seat) => seat.heldByYou).map((seat) => seat.priceCents),
  );

  const tierLegend = useMemo(() => {
    const seen = new Map<string, number>();
    for (const seat of map?.seats ?? []) {
      if (seat.type !== 'SEAT') continue;
      if (!seen.has(seat.tierName)) seen.set(seat.tierName, seat.priceCents);
    }
    return [...seen.entries()].map(([tierName, priceCents]) => ({ tierName, priceCents }));
  }, [map]);

  const expiresIn = map?.holdExpiresAt ? new Date(map.holdExpiresAt).getTime() - now : 0;
  const holdLapsed = Boolean(map?.holdExpiresAt) && expiresIn <= 0;

  useEffect(() => {
    if (holdLapsed) void load();
  }, [holdLapsed, load]);

  async function toggle(seat: SeatMapSeat) {
    if (seat.state === 'BOOKED') return;
    if (seat.state === 'HELD' && !seat.heldByYou) return;

    setBusy(seat.code);
    setError(null);
    try {
      if (seat.heldByYou) {
        await api.request(`/public/trips/${tripId}/holds/${seat.code}`, { method: 'DELETE', auth: false });
        announce(`Seat ${seat.code} released`);
      } else {
        await api.request(`/public/trips/${tripId}/holds`, {
          method: 'POST',
          auth: false,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ seatCode: seat.code }),
        });
        announce(`Seat ${seat.code} selected`);
      }
      await load();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.code === 'SEAT_TAKEN'
            ? `Seat ${seat.code} was just taken by someone else.`
            : err.message
          : 'Something went wrong. Try again.';
      setError(message);
      await load();
    } finally {
      setBusy(null);
    }
  }

  function announce(message: string) {
    if (liveRegion.current) liveRegion.current.textContent = message;
  }

  if (error && !map) {
    return (
      <section className="px-4 py-8">
        <Alert tone="error">{error}</Alert>
      </section>
    );
  }

  if (!map || !trip) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner label={t('s.loading')} />
      </div>
    );
  }

  const grid = [...map.seats].sort((a, b) => a.row - b.row || a.col - b.col);

  return (
    <section className="px-4 py-6 pb-32">
      <Eyebrow>{t('s.choose')}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{trip.routeName}</h1>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
        {map.seatsLeft} of {map.capacity} seats available · {trip.busName}
      </p>

      {expiresIn > 0 && (
        <p className="mt-3 text-sm font-medium tabular" style={{ color: 'var(--signal-delayed)' }}>
          Seats held for {formatCountdown(expiresIn)}
        </p>
      )}

      {error && (
        <div className="mt-3">
          <Alert tone="warning">{error}</Alert>
        </div>
      )}

      <p ref={liveRegion} className="sr-only" role="status" aria-live="polite" />

      <div
        className="mt-5 inline-block rounded-2xl border p-4"
        style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
      >
        <p
          className="mb-3 text-center text-[0.625rem] uppercase tracking-[0.2em]"
          style={{ color: 'var(--color-faint)' }}
        >
          {t('s.front')}
        </p>
        <div
          className="grid gap-1.5"
          style={{ gridTemplateColumns: `repeat(${map.columns}, 2.5rem)` }}
          role="group"
          aria-label={t('s.seatMap')}
        >
          {grid.map((seat) => {
            if (seat.type !== 'SEAT') {
              return (
                <span
                  key={`${seat.row}:${seat.col}`}
                  className="grid h-10 w-10 place-items-center text-[0.625rem]"
                  style={{ color: 'var(--color-faint)' }}
                  aria-hidden
                >
                  {seat.type === 'DRIVER' ? '⚙' : seat.type === 'DOOR' ? '🚪' : '·'}
                </span>
              );
            }

            const unavailable = seat.state === 'BOOKED' || (seat.state === 'HELD' && !seat.heldByYou);
            const seatPrice = money(seat.priceCents, settings.currency);
            const label = seat.heldByYou
              ? `Seat ${seat.code}, selected, ${seatPrice}. Activate to release.`
              : unavailable
                ? `Seat ${seat.code}, unavailable`
                : `Seat ${seat.code}, available, ${seatPrice} (${seat.tierName}). Activate to select.`;

            return (
              <button
                key={seat.code}
                type="button"
                onClick={() => void toggle(seat)}
                disabled={unavailable || busy !== null}
                aria-label={label}
                aria-pressed={seat.heldByYou}
                title={unavailable ? undefined : `${seat.tierName} · ${seatPrice}`}
                className="grid h-10 w-10 place-items-center rounded-lg border text-[0.6875rem] font-semibold tabular transition-transform active:scale-95 disabled:cursor-not-allowed"
                style={{
                  background: seat.heldByYou
                    ? 'var(--color-primary)'
                    : unavailable
                      ? 'transparent'
                      : // A wash, not the solid swatch, so the seat code keeps its contrast in both themes.
                        `color-mix(in srgb, ${hashTierColor(seat.tierName)} 28%, var(--color-bg))`,
                  color: seat.heldByYou
                    ? 'var(--color-on-primary)'
                    : unavailable
                      ? 'var(--color-faint)'
                      : 'var(--color-text)',
                  borderColor: seat.heldByYou ? 'var(--color-primary)' : 'var(--color-line-strong)',
                  opacity: unavailable ? 0.4 : 1,
                  textDecoration: unavailable ? 'line-through' : 'none',
                }}
              >
                {seat.heldByYou ? (
                  <span className="flex flex-col items-center leading-none">
                    <span>{seat.code}</span>
                    <span className="money text-[0.5rem] font-normal" style={{ opacity: 0.85 }}>
                      {seatPrice}
                    </span>
                  </span>
                ) : (
                  seat.code
                )}
              </button>
            );
          })}
        </div>
      </div>

      <ul className="mt-4 flex flex-wrap gap-4 text-xs" style={{ color: 'var(--color-muted)' }}>
        <li className="flex items-center gap-1.5">
          <span
            className="h-3.5 w-3.5 rounded border"
            style={{ borderColor: 'var(--color-line-strong)', background: 'var(--color-bg)' }}
          />
          {t('s.available')}
        </li>
        <li className="flex items-center gap-1.5">
          <span className="h-3.5 w-3.5 rounded" style={{ background: 'var(--color-primary)' }} />
          {t('s.yours')}
        </li>
        <li className="flex items-center gap-1.5">
          <span
            className="h-3.5 w-3.5 rounded border opacity-40"
            style={{ borderColor: 'var(--color-line-strong)' }}
          />
          {t('s.taken')}
        </li>
      </ul>

      {tierLegend.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-4 text-xs" style={{ color: 'var(--color-muted)' }}>
          {tierLegend.map(({ tierName, priceCents }) => (
            <li key={tierName} className="flex items-center gap-1.5">
              <span
                className="h-3.5 w-3.5 rounded-full border"
                style={{ background: hashTierColor(tierName), borderColor: 'var(--color-line)' }}
                aria-hidden
              />
              {tierName} · {money(priceCents, settings.currency)}
            </li>
          ))}
        </ul>
      )}

      {selected.length > 0 && (
        <div
          // Sits directly above the tab bar rather than under it: covering the
          // primary action would make checkout unreachable on a phone.
          className="fixed inset-x-0 z-40 border-t px-4 pb-3 pt-3"
          style={{
            bottom: 'var(--app-bottom-inset, 0px)',
            background: 'var(--color-bg)',
            borderColor: 'var(--color-line)',
          }}
        >
          <div className="mx-auto flex max-w-[640px] items-center gap-4">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {selected.length} seat{selected.length === 1 ? '' : 's'}: {selected.join(', ')}
              </p>
              <p className="money text-xs" style={{ color: 'var(--color-muted)' }}>
                {money(total, settings.currency)} total
              </p>
            </div>
            <Button
              size="lg"
              onClick={() =>
                navigate(
                  `/checkout?trip=${tripId}&from=${fromStopId}&to=${toStopId}&seats=${selected.join(',')}`,
                )
              }
            >
              {t('c.continue')}
            </Button>
          </div>
        </div>
      )}

      {selected.length === 0 && wanted > 1 && (
        <p className="mt-4 text-sm" style={{ color: 'var(--color-muted)' }}>
          Pick {wanted} seats to continue.
        </p>
      )}
    </section>
  );
}
